"use client"

import type React from "react"

import { createContext, useContext, useState, useEffect } from "react"
import { useRouter } from "next/navigation"
import { CRM_API_BASE } from "@/lib/config"
import { httpJson, cancelAllRequests } from "@/lib/http"

interface User {
  id: string
  email: string
  name: string
  company_name?: string
  token: string
  role: "internal" | "organization" | "client"
  permissions?: string[]
  clientId?: string // For client users, this will be the related client ID
}

interface AuthContextType {
  user: User | null
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>
  logout: () => void
  isLoading: boolean
  isAuthenticated: boolean
  hasRole: (role: string) => boolean
  hasPermission: (permission: string) => boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Helper function to decode JWT token safely
function decodeJWT(token: string) {
  try {
    const base64Url = token.split(".")[1]
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/")
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    )
    return JSON.parse(jsonPayload)
  } catch (error) {
    console.error("❌ Error decoding JWT:", error)
    return null
  }
}

// Helper function to check if token is expired
function isTokenExpired(token: string): boolean {
  try {
    const payload = decodeJWT(token)
    if (!payload || !payload.exp) return true

    const currentTime = Math.floor(Date.now() / 1000)
    return payload.exp < currentTime
  } catch {
    return true
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const router = useRouter()

  // Helper function to clear authentication data from localStorage
  const clearAuthData = () => {
    localStorage.removeItem("auth_token")
    localStorage.removeItem("refresh_token")
    localStorage.removeItem("user_data")
  }

  useEffect(() => {
    // Check for stored auth token on mount
    const token = localStorage.getItem("auth_token") || null
    const refreshToken = localStorage.getItem("refresh_token") || null
    const userData = localStorage.getItem("user_data") || null

    console.log("🔍 [v0] AuthProvider initialization check...")
    console.log("🔍 [v0] Token exists:", !!token)
    console.log("🔍 [v0] User data exists:", !!userData)
    console.log("🔍 [v0] Token preview:", token ? token.substring(0, 20) + "..." : "none")

    try {
      if (token && userData) {
        // Check if token is expired
        if (isTokenExpired(token)) {
          clearAuthData()
          return
        }

        // Parse and restore user session
        if (userData && userData !== "undefined" && userData !== "null") {
          const parsedUser = JSON.parse(userData)
          if (parsedUser) {
            const userWithToken = { ...parsedUser, token }
            setUser(userWithToken)

            if (typeof window !== "undefined" && window.FS) {
              window.FS('setIdentity', {
                uid: parsedUser.id,
                properties: {
                  displayName: parsedUser.name,
                  email: parsedUser.email,
                }
              });
            }
          }
        } else {
          console.log("⚠️ [v0] No valid user data found in localStorage")
        }
      } else {
        console.log("ℹ️ [v0] No stored authentication found")
      }
    } catch (error) {
      console.error("❌ [v0] Error parsing stored user data:", error)
      clearAuthData()
    } finally {
      // Always set loading to false after checking authentication
      setIsLoading(false)
    }
  }, [])

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log("🔐 AuthProvider: Starting login process...")
      console.log("📧 Email:", email)
      console.log("🔒 Password length:", password.length)

      // Validate input
      if (!email || !password) {
        console.error("❌ Missing credentials")
        return { success: false, error: "Email and password are required" }
      }

      if (!email.includes("@")) {
        console.error("❌ Invalid email format")
        return { success: false, error: "Please enter a valid email address" }
      }

      console.log("📡 Making request to auth endpoint...")
      try {
        const data = await httpJson<{ access?: string; refresh?: string; user?: any }>(`${CRM_API_BASE}/auth/token/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "User-Agent": "CRM-Frontend/1.0",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify({ email, password }),
        })

        console.log("✅ Authentication response received")
        console.log("🔍 Response data keys:", Object.keys(data))

        // Handle the API response format: { access: "...", refresh: "...", user: {...} }
        if (data.access) {
          console.log("✅ Access token found in response")
          console.log("🔑 Token preview:", data.access.substring(0, 20) + "...")

          // Decode the JWT to get user information
          const tokenPayload = decodeJWT(data.access)
          console.log("🔍 Token payload:", tokenPayload)

          const backendUser = data.user || {}
          console.log("🔍 [v0] Backend user object:", backendUser)
          console.log("🔍 [v0] Backend user role:", backendUser.role)
          console.log("🔍 [v0] Token payload role:", tokenPayload?.role)
          console.log("🔍 [v0] Email being authenticated:", email)

          const role = backendUser.role || tokenPayload?.role || "internal"
          console.log("🔍 [v0] Using backend-provided role:", role)

          const permissions = backendUser.permissions || tokenPayload?.permissions || []

          // Extract client ID for client users
          // For client users, we need to find the related client record ID, not the user ID
          let clientId = undefined
          
          if (role === "client") {
            // Try to get client ID from token payload clients array first
            if (tokenPayload?.clients && Array.isArray(tokenPayload.clients) && tokenPayload.clients.length > 0) {
              clientId = tokenPayload.clients[0].id?.toString()
              console.log("🔍 [v0] Found client ID from token payload clients array:", clientId)
            } else {
              // Fallback to other fields
              clientId = tokenPayload?.client_id || backendUser.client_id || tokenPayload?.related_client_id || backendUser.related_client_id
            }
            
            // If no client ID found in token/user data, we'll need to fetch it separately
            if (!clientId) {
              console.log("🔍 [v0] No client ID found in token/user data, will need to fetch it separately")
            }
          }

          console.log("🔍 [v0] Client ID extraction for role:", role)
          console.log("🔍 [v0] Token payload clients array:", tokenPayload?.clients)
          console.log("🔍 [v0] Token payload client_id:", tokenPayload?.client_id)
          console.log("🔍 [v0] Token payload related_client_id:", tokenPayload?.related_client_id)
          console.log("🔍 [v0] Backend user client_id:", backendUser.client_id)
          console.log("🔍 [v0] Backend user related_client_id:", backendUser.related_client_id)
          console.log("🔍 [v0] User ID (should NOT be used as client ID):", tokenPayload?.user_id || backendUser.id)
          console.log("🔍 [v0] Final clientId:", clientId)

          // Extract company_name from token payload for client users
          let companyName = backendUser.company_name
          if (role === "client" && tokenPayload?.clients && Array.isArray(tokenPayload.clients) && tokenPayload.clients.length > 0) {
            companyName = tokenPayload.clients[0].company_name || backendUser.company_name
            console.log("🔍 [v0] Extracted company_name from token:", companyName)
          }

          const userData = {
            id: tokenPayload?.user_id || backendUser.id || "1",
            email: email,
            name: backendUser.name || email.split("@")[0],
            company_name: companyName,
            role: role as "internal" | "organization" | "client",
            permissions: permissions,
            clientId: clientId,
          }

          console.log("💾 Storing authentication data...")
          console.log("🔍 Final user role assigned:", role)
          // Store auth data securely
          localStorage.setItem("auth_token", data.access)
          if (data.refresh) {
            localStorage.setItem("refresh_token", data.refresh)
            console.log("💾 Refresh token stored")
          }
          localStorage.setItem("user_data", JSON.stringify(userData))

          setUser({ ...userData, token: data.access })

          if (typeof window !== "undefined" && window.FS) {
            window.FS('setIdentity', {
              uid: parsedUser.id,
              properties: {
                displayName: parsedUser.name,
                email: parsedUser.email,
              }
            });
          }

          return { success: true }
        } else {
          console.error("❌ Invalid response format - missing access token")
          console.error("❌ Response data:", data)
          return { success: false, error: "Invalid response from authentication server" }
        }
      } catch (error) {
        // Use console.warn instead of console.error to avoid Next.js unhandled error detection
        console.warn("❌ Authentication error:", error)
        console.warn("❌ Error type:", typeof error)
        console.warn("❌ Error message:", error instanceof Error ? error.message : "Unknown error")

        return {
          success: false,
          error: error instanceof Error ? error.message : "Network error occurred",
        }
      }
    } catch (error) {
      console.warn("❌ Unexpected error in login:", error)
      return {
        success: false,
        error: "An unexpected error occurred",
      }
    }
  }

  const logout = () => {
    console.log("🚪 Logging out user securely")

    // Cancel all pending HTTP requests first
    cancelAllRequests()

    // Clear all possible auth data immediately
    localStorage.removeItem("auth_token")
    localStorage.removeItem("refresh_token")
    localStorage.removeItem("user_data")

    // Also clear any other potential auth keys
    localStorage.removeItem("tokenKey")
    localStorage.removeItem("userKey")

    // Clear Gmail tokens if they exist
    localStorage.removeItem("gmail_access_token")
    localStorage.removeItem("gmail_refresh_token")
    localStorage.removeItem("gmail_token_expires_at")
    localStorage.removeItem("gmail_user_info")

    // Reset user state
    setUser(null)

    // Navigate to login
    router.replace("/login")

    console.log("✅ All authentication data cleared and pending requests cancelled")
  }

  const hasRole = (role: string): boolean => {
    return user?.role === role
  }

  const hasPermission = (permission: string): boolean => {
    return user?.permissions?.includes(permission) || false
  }

  const value = {
    user,
    login,
    logout,
    isLoading,
    isAuthenticated: !!user,
    hasRole,
    hasPermission,
  }

  console.log("🔍 [v0] AuthProvider render - user:", !!user, "isLoading:", isLoading, "isAuthenticated:", !!user)

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
